// Firebase Configuration for Push Notifications
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { API_URL } from '../services/apiConfig';

const firebaseConfig = {
  apiKey: "AIzaSyBXkZ9sqqjmNyOgIYGyz9DlvP-Ck4kQq3k",
  authDomain: "golok-kosh-3.firebaseapp.com",
  projectId: "golok-kosh-3",
  storageBucket: "golok-kosh-3.firebasestorage.app",
  messagingSenderId: "612979997015",
  appId: "1:612979997015:web:5ba3a0cbee15113157e612"
};

// VAPID Key for Web Push
const VAPID_KEY = "BMJmqMpRMrPHTe9-oBQsE2wIV81W3guDXA8e8X6cE31VFwzbSNobJy27t5t2ZW9sZEpDS_w_l33G8S-XYAIl5XY";

// Initialize Firebase (singleton pattern)
export const getFirebaseApp = () => {
  return getApps()[0] || initializeApp(firebaseConfig);
};

// Get Firebase Messaging instance
let messagingInstance: Messaging | null = null;

export const getFirebaseMessaging = (): Messaging | null => {
  if (typeof window === 'undefined') return null;
  
  // In Capacitor, use native push notifications, not Firebase web messaging
  try {
    const { Capacitor } = require('@capacitor/core');
    if (Capacitor.isNativePlatform()) return null;
  } catch {}
  
  // Check if browser supports notifications
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return null;
  }
  
  if (!messagingInstance) {
    try {
      const app = getFirebaseApp();
      messagingInstance = getMessaging(app);
    } catch (error) {
      console.error('Error initializing Firebase Messaging:', error);
      return null;
    }
  }
  
  return messagingInstance;
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async (): Promise<string | null> => {
  try {
    // In Capacitor, skip Firebase web messaging
    try {
      const { Capacitor } = require('@capacitor/core');
      if (Capacitor.isNativePlatform()) return null;
    } catch {}

    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return null;
    }
    
    // Check current permission - don't request again if already granted
    let permission = Notification.permission;
    
    if (permission === 'default') {
      // Only request if not yet decided
      permission = await Notification.requestPermission();
    }
    
    if (permission !== 'granted') {
      console.log('Notification permission not granted:', permission);
      return null;
    }
    
    // Wait for Firebase Messaging service worker to be ready
    // This is CRITICAL - getToken needs an active service worker
    let swRegistration: ServiceWorkerRegistration | undefined;
    
    if ('serviceWorker' in navigator) {
      try {
        // First, try to get existing registration
        const registrations = await navigator.serviceWorker.getRegistrations();
        console.log('Found service workers:', registrations.length);
        
        // Look for firebase messaging SW - check both active and installing/waiting
        swRegistration = registrations.find(r => {
          const scriptURL = r.active?.scriptURL || r.installing?.scriptURL || r.waiting?.scriptURL || '';
          return scriptURL.includes('firebase-messaging-sw.js');
        });
        
        // If not found, register it
        if (!swRegistration) {
          console.log('Registering Firebase Messaging SW...');
          swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
            scope: '/'
          });
          
          // Wait for the service worker to be active
          if (swRegistration.installing) {
            await new Promise<void>((resolve) => {
              swRegistration!.installing!.addEventListener('statechange', function() {
                if (this.state === 'activated') {
                  resolve();
                }
              });
            });
          } else {
            await navigator.serviceWorker.ready;
          }
        }
        
        console.log('Firebase SW Registration state:', swRegistration?.active?.state || 'no active worker');
      } catch (swError: any) {
        console.error('Service Worker registration error:', swError?.message || swError);
        // Continue without SW - some features may not work
      }
    }
    
    // Get messaging instance
    const messaging = getFirebaseMessaging();
    if (!messaging) {
      console.log('Failed to get messaging instance');
      throw new Error('Firebase Messaging not available');
    }
    
    // Get FCM token with service worker registration
    const tokenOptions: { vapidKey: string; serviceWorkerRegistration?: ServiceWorkerRegistration } = {
      vapidKey: VAPID_KEY
    };
    
    if (swRegistration) {
      tokenOptions.serviceWorkerRegistration = swRegistration;
    }
    
    console.log('Requesting FCM token...');
    const token = await getToken(messaging, tokenOptions);
    
    if (token) {
      console.log('FCM Token obtained:', token.substring(0, 20) + '...');
      return token;
    }
    
    console.log('No FCM token received');
    return null;
  } catch (error: any) {
    console.error('Error getting notification permission:', error?.message || error);
    throw error; // Re-throw so caller can handle it
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback: (payload: any) => void) => {
  const messaging = getFirebaseMessaging();
  if (!messaging) return () => {};
  
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};

// Save FCM token to backend
export const saveFCMTokenToBackend = async (token: string, userId?: string): Promise<boolean> => {
  try {
      const sessionToken = localStorage.getItem('session_token');
    
    const response = await fetch(`${API_URL}/api/notifications/register-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({
        fcm_token: token,
        user_id: userId,
        device_type: /Mobile|Android|iPhone/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        platform: navigator.platform
      })
    });
    
    return response.ok;
  } catch (error) {
    console.error('Error saving FCM token:', error);
    return false;
  }
};
